import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tabs, Tab, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listVatRates, deactivateVatRate, listPackagingRates, deactivatePackagingRate, type VatRate, type PackagingTaxRate } from '../../api/taxe'
import { listExpenseCategories, updateExpenseCategory, type ExpenseCategory } from '../../api/deconturi'
import { errorMessage } from '../../api/client'
import VatRateFormDialog from './VatRateFormDialog'
import PackagingTaxFormDialog from './PackagingTaxFormDialog'
import ExpenseCategoryFormDialog from './ExpenseCategoryFormDialog'

const CATEGORY_LABELS: Record<string, string> = {
  Plastic: 'Plastic', HartieCarton: 'Hârtie / carton', Sticla: 'Sticlă', Metal: 'Metal', Lemn: 'Lemn', Altele: 'Altele',
}
const TREATMENT_LABELS: Record<string, string> = {
  Standard: 'Standard', CotaRedusa: 'Cotă redusă', TaxareInversa: 'Taxare inversă',
  Scutit: 'Scutit', IntracomunitarUE: 'Intracomunitar UE', Export: 'Export',
}

export default function TaxePage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Configurare</Typography>
        <Typography variant="inherit" color="text.primary">Taxe</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Reguli de taxare</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Cote TVA" />
        <Tab label="Taxă ambalaje (Fond Mediu)" />
        <Tab label="Categorii de cheltuială" />
      </Tabs>
      {tab === 0 ? <VatRatesTab /> : tab === 1 ? <PackagingRatesTab /> : <ExpenseCategoriesTab />}
    </Stack>
  )
}

function VatRatesTab() {
  const [formTarget, setFormTarget] = useState<VatRate | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: VatRate } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'cote-tva'], queryFn: listVatRates })
  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivateVatRate,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['financiar', 'cote-tva'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<VatRate>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 120, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'ratePercent', headerName: 'Cotă', width: 90, renderCell: (p) => `${p.row.ratePercent}%` },
    { field: 'treatmentType', headerName: 'Tratament', width: 160, renderCell: (p) => TREATMENT_LABELS[p.row.treatmentType] ?? p.row.treatmentType },
    { field: 'effectiveFrom', headerName: 'De la', width: 110 },
    { field: 'effectiveTo', headerName: 'Până la', width: 110, renderCell: (p) => p.row.effectiveTo ?? '—' },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activă" size="small" color="success" variant="outlined" />
        : <Chip label="Inactivă" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă cotă TVA</Button>
      </Box>
      <Box sx={{ height: 460, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <VatRateFormDialog rate={formTarget} onClose={() => setFormTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}

function PackagingRatesTab() {
  const [formTarget, setFormTarget] = useState<PackagingTaxRate | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: PackagingTaxRate } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'taxa-ambalaje'], queryFn: listPackagingRates })
  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivatePackagingRate,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['financiar', 'taxa-ambalaje'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<PackagingTaxRate>[]>(() => [
    { field: 'materialCategory', headerName: 'Categorie material', width: 180, renderCell: (p) => CATEGORY_LABELS[p.row.materialCategory] ?? p.row.materialCategory },
    { field: 'ratePerKg', headerName: 'Tarif / kg', width: 120, renderCell: (p) => `${p.row.ratePerKg} ${p.row.currency}` },
    { field: 'effectiveFrom', headerName: 'De la', width: 110 },
    { field: 'effectiveTo', headerName: 'Până la', width: 110, renderCell: (p) => p.row.effectiveTo ?? '—' },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activă" size="small" color="success" variant="outlined" />
        : <Chip label="Inactivă" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă taxă ambalaje</Button>
      </Box>
      <Box sx={{ height: 460, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <PackagingTaxFormDialog rate={formTarget} onClose={() => setFormTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}

function ExpenseCategoriesTab() {
  const [formTarget, setFormTarget] = useState<ExpenseCategory | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: ExpenseCategory } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'categorii-cheltuieli'], queryFn: listExpenseCategories })
  const closeMenu = () => setMenuAnchor(null)

  const toggleActiveMutation = useMutation({
    mutationFn: (row: ExpenseCategory) =>
      updateExpenseCategory(row.id, { code: row.code, name: row.name, deductible: row.deductible, isActive: !row.isActive }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['financiar', 'categorii-cheltuieli'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<ExpenseCategory>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 140, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    {
      field: 'deductible', headerName: 'Deductibilă', width: 120, sortable: false,
      renderCell: (p) => p.row.deductible
        ? <Chip label="Da" size="small" color="success" variant="outlined" />
        : <Chip label="Nu" size="small" variant="outlined" />,
    },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activă" size="small" color="success" variant="outlined" />
        : <Chip label="Inactivă" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă categorie</Button>
      </Box>
      <Box sx={{ height: 460, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <ExpenseCategoryFormDialog category={formTarget} onClose={() => setFormTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="toggle" onClick={() => { closeMenu(); toggleActiveMutation.mutate(menuAnchor.row) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon>
            <ListItemText>{menuAnchor.row.isActive ? 'Dezactivare' : 'Reactivare'}</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
